import { Avatar, Icon, SelectOutlined } from '@amelio/ui-library';

import type { TranslationKey } from '@/i18n-key';
import type { PortalAudience, PortalPerson } from '@/pages/EmployeePortal/hooks/usePortalAudience';

import { useAmelioTranslations } from '@/hooks';

/** Past eight rows the list scrolls, and finding someone is quicker by typing their name. */
const MAX_UNSEARCHED_OPTIONS = 8;

/** Wide enough for a full name and the line under the reader's own, beside a narrower trigger. */
const MENU_MIN_WIDTH = 280;

/**
 * The surface the cycle lens on the same row wears -- translucent white, white hairline, blurred.
 * Spelled on the trigger slots rather than through `TOOLBAR_CONTROL_SURFACE`, whose `!` form is
 * meant for controls that paint their own opaque background over it.
 */
const TOOLBAR_SURFACE = {
    background: 'bg-white/70 hover:bg-white/90',
    border: 'border border-white',
};

/**
 * "Voir le portail": the picker a manager opens an employee's portal from, at the right end of the
 * tab row. Their own portal is one of the choices, so the way back is where the way out was.
 *
 * It holds nothing: the audience decides who is listed and what picking someone does, and the
 * chosen employee lives in the address. Empty, the trigger reads "Voir le portail" -- the reader is
 * on their own portal, which is what the portal opens on.
 */
export const ViewPortalSelect = ({
    people,
    isError,
    selectedId,
    openPortalOf,
}: Pick<PortalAudience, 'people' | 'isError' | 'selectedId' | 'openPortalOf'>) => {
    const { t } = useAmelioTranslations();

    const toEntry = (person: PortalPerson) => ({
        id: person.id,
        value: person.id,
        label: person.name,
        description: person.isReader ? t('employeePortal.viewPortal.own') : undefined,
        startIcon: (
            <Avatar
                src={person.avatarUrl || undefined}
                alt={''}
                initials={person.initials}
                size={'4xs'}
            />
        ),
    });

    const options = (people ?? []).filter((person) => !person.isReader).map(toEntry);
    // Pinned under the list and outside its scroll, and left out of the search: in a company where
    // the reader reaches fifty people, their own name would otherwise sit somewhere in the middle
    // of the alphabet, and the way back would have to be looked for.
    const ownEntry = people?.find((person) => person.isReader);
    const footerOptions = ownEntry ? [toEntry(ownEntry)] : undefined;

    // Three ways to have nothing to offer, and the reader is told which. A list still on its way is
    // empty too, so "nobody to show" would otherwise state as fact something nobody has checked.
    const emptyReason = (): TranslationKey => {
        if (isError) return 'employeePortal.viewPortal.loadError';
        if (people === undefined) return 'employeePortal.viewPortal.label';
        return 'employeePortal.viewPortal.none';
    };

    return (
        <div className={'max-w-[220px]'}>
            <SelectOutlined
                options={options}
                footerOptions={footerOptions}
                value={selectedId}
                onChange={(employeeId) => openPortalOf(String(employeeId))}
                placeholder={t(
                    options.length > 0 ? 'employeePortal.viewPortal.label' : emptyReason(),
                )}
                disabled={options.length === 0}
                searchable={options.length > MAX_UNSEARCHED_OPTIONS}
                searchPlaceholder={t('employeePortal.viewPortal.search')}
                emptyText={t('employeePortal.viewPortal.noMatch')}
                showSelectedStartIcon
                menuMinWidth={MENU_MIN_WIDTH}
                // The trigger's text is the chosen employee once there is one, so the control keeps
                // saying what it does through its name rather than through what it shows.
                ariaLabel={t('employeePortal.viewPortal.label')}
                leadingIcon={
                    <Icon
                        name={'users-user-search'}
                        size={'sm'}
                        colorClassName={'text-obj-slate'}
                        ariaHidden
                    />
                }
                triggerBackgroundClassName={TOOLBAR_SURFACE.background}
                triggerBorderClassName={TOOLBAR_SURFACE.border}
                triggerClassName={'backdrop-blur-sm'}
                textClassName={'text-navy'}
                dataTestId={'employee-portal-view-portal'}
            />
        </div>
    );
};
